import Joi from 'joi';
import type { IRecognitionResult } from './recognitionResult';
import { recognitionResultSchema } from './recognitionResult';
import type { Revision } from '../configuration';
import { revisionSchema } from '../configuration';
import type { SchemaOf } from '../helpers';
import { makeAllNumbersUnsafe, objectSchemaOf } from '../helpers';

export interface IRecognitionResultMap {
  [key: string]: IRecognitionResult | undefined;
}

export interface IRecordingHeader {
  recordingId: string;
  consoleId: string;
  conversationId: string;
  name: string;
  timestamp: number;
  sampleRate: number;
  lengthSeconds: number;
  size: number;
  url: string;
  cloudConsoleUrl?: string;
  // this is time since start of conversation (or connection, when disrupted)
  timeSinceStartSeconds: number;
  // this is start time of the utterance in this recording:
  // when no utterance is detected, this will be undefined:
  timeToUtteranceSeconds?: number;
  // this is final moment of the utterance in this recording:
  utteranceLengthSeconds?: number;

  microphoneDescription: string;
  consoleVersion: string;
  serverVersion: string;
  browserName?: string;
  browserVersion?: string;
  browserPlatform?: string;
  isLab: boolean;
  configRevision: Revision;

  tags?: string[];
  script?: string;
  recognitionResults?: IRecognitionResultMap;
  isFullConversation?: boolean;
}

const recordingSchemaMap: SchemaOf<IRecordingHeader> = {
  recordingId: Joi.string().required(),
  consoleId: Joi.string().required(),
  conversationId: Joi.string().required(),
  name: Joi.string().required(),
  timestamp: Joi.number().required(),
  sampleRate: Joi.number().required(),
  lengthSeconds: Joi.number().required(),
  size: Joi.number().required(),
  url: Joi.string().required(),
  cloudConsoleUrl: Joi.string(),
  timeSinceStartSeconds: Joi.number().required(),
  timeToUtteranceSeconds: Joi.number(),
  utteranceLengthSeconds: Joi.number(),

  microphoneDescription: Joi.string().required(),
  consoleVersion: Joi.string().required(),
  serverVersion: Joi.string().required(),
  browserName: Joi.string().required().allow(''),
  browserVersion: Joi.string().required().allow(''),
  browserPlatform: Joi.string().required().allow(''),
  isLab: Joi.boolean().required(),
  configRevision: revisionSchema,

  tags: Joi.array().items(Joi.string().not().empty()).optional(),
  script: Joi.string().optional(),
  recognitionResults: Joi.object().pattern(
    Joi.string(),
    recognitionResultSchema
  ),
  isFullConversation: Joi.bool().optional(),
};

export const bucketRecordingSchemaBase = Joi.object(
  makeAllNumbersUnsafe(recordingSchemaMap)
);

export const recordingSchema = objectSchemaOf<IRecordingHeader>(
  recordingSchemaMap
);
